/**
 * @module
 * @author Bayu Dwiyan Satria
 * @since 1.0.0
 */

/**
 * Config
 */
import {
  aboutPage,
  authorPage,
  contactFaq,
  contactPage,
  documentationPage,
  homePage,
  notFoundPage,
  primaryEntity,
  softwareEntities,
  website
} from '@/config/entities'
import { siteConfig } from '@/config/site'

/**
 * Types
 */
import type { RouteRecordRaw } from 'vue-router'

/**
 * The application's routes.
 *
 * @remarks
 * This table is the single source of truth for far more than navigation. Each entry's `meta` drives
 * the document title, the meta description, the robots directive and the JSON-LD emitted into that
 * page's prerendered HTML — and the set of paths here determines which HTML files the build
 * produces, which in turn determines what lands in `sitemap.xml`.
 *
 * Two conventions are load-bearing:
 *
 * 1. **Titles and descriptions are read off the schema objects**, not retyped. `<title>` and the
 *    page entity's `name` describing the same page differently is a contradiction a crawler can
 *    see, and copying strings between two files guarantees it eventually happens.
 *
 * 2. **Site-wide entities appear on the home route only.** `primaryEntity` and
 *    `website` anchor the graph; repeating them on every route would declare the same
 *    organisation once per page instead of once per site.
 *
 * Every component is lazily imported so each route becomes its own chunk.
 *
 * @example
 * Adding a route — nothing else needs editing; the prerendered HTML, the canonical URL and the
 * sitemap entry all follow:
 * ```ts
 * {
 *   path: '/pricing',
 *   name: 'Pricing',
 *   component: () => import('@/views/PricingView.vue'),
 *   meta: { title: 'Pricing', description: 'What it costs.', schema: [pricingPage] }
 * }
 * ```
 */
export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/HomeView.vue'),
    meta: {
      title: siteConfig.title,
      description: siteConfig.description,
      /*
       * The home title is already the full brand string; running it through '%s | Site' would
       * repeat the site name twice in one tab.
       */
      applyTitleTemplate: false,
      /*
       * The software entities sit here with the other site-wide ones: there is one product, and the
       * home route is where it is described. Spread rather than listed, because a site that is not
       * about software configures none and the array is empty.
       */
      schema: [primaryEntity, website, homePage, ...softwareEntities],
      sitemap: { priority: 1.0, changefreq: 'weekly' }
    }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('@/views/AboutView.vue'),
    meta: {
      title: aboutPage.name as string,
      description: aboutPage.description as string,
      schema: [aboutPage],
      sitemap: { priority: 0.8, changefreq: 'monthly' }
    }
  },
  {
    path: '/author',
    name: 'Author',
    component: () => import('@/views/AuthorView.vue'),
    meta: {
      title: authorPage.name as string,
      description: authorPage.description as string,
      schema: [authorPage],
      sitemap: { priority: 0.5, changefreq: 'monthly' }
    }
  },
  {
    path: '/documentation',
    name: 'Documentation',
    component: () => import('@/views/DocumentationView.vue'),
    meta: {
      title: documentationPage.name as string,
      description: documentationPage.description as string,
      schema: [documentationPage],
      sitemap: { priority: 0.7, changefreq: 'monthly' }
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('@/views/ContactView.vue'),
    meta: {
      title: contactPage.name as string,
      description: contactPage.description as string,
      schema: [contactPage, contactFaq],
      sitemap: { priority: 0.6, changefreq: 'yearly' }
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/error/NotFoundView.vue'),
    meta: {
      title: 'Page not found',
      description: 'The page you are looking for does not exist or has moved.',
      /*
       * `follow` rather than `nofollow`: there is no reason to strand a crawler that arrived via
       * a stale link — let it walk out through the navigation.
       */
      robots: 'noindex, follow',
      schema: [notFoundPage],
      sitemap: false
    }
  }
]
